import React, { useState, useEffect, useCallback } from 'react';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Col, Row, Card } from 'antd';
import WalletIcon from '../../assets/icons/wallet-icon';
import TempUserIcon from '../../assets/icons/temp-user-icon';
import CoinIcon from '../../assets/icons/coin-icon';
import image from '../../assets/images/user-image.png';
import { StatCards } from './StatCards';
import { hideLoader } from '../../utils/loader';
import { customBaseUrl, httpGet } from '../../store/http';
import { currencyFormat } from '../../utils/helper';
import { useNavigate } from 'react-router-dom';

const styles = {
  recentCard: { marginTop: 5 },
  recentList: { display: 'flex', justifyContent: 'space-between', marginBottom: 38 },
  title: { fontStyle: 'normal', fontSize: 12, fontWeight: 500 },
  btn: { width: 87, height: 24, backgroundColor: '#121212', borderRadius: 2, color: '#ffffff', fontSize: 10, margin: 0 },
  listItem: { display: 'flex', justifyContent: 'space-between', marginBottom: 22 },
  userColItem: { display: 'flex' },
  userCol: { flexBasis: '50%' },
  name: { fontStyle: 'normal', fontWeight: '500', fontSize: 12, lineHeight: '15px', color: '#000000' },
  phone: { fontStyle: 'normal', fontWeight: '500', fontSize: 12, lineHeight: '15px', color: '#BDBDBD' },
  btnCol: { width: 99, height: 29, background: '#F5F5F5', border: '1px solid #D5D5D5', borderRadius: 3, fontWeight: 500, fontSize: 12, color: '#000000' },
  recentImg: { width: 38, height: 38, borderRadius: 38, marginRight: 8 },
};

function DashboardOverView({ state, analysis, tierData }) {
  const navigate = useNavigate();

  const [totalRegUsers, setTotalRegisteredUsers] = useState(0);
  const [totalCorporateUsers, setTotalCorporateUsers] = useState(0);
  const [totalIndividualUsers, setTotalIndividualUsers] = useState(0);
  const [activeUsers, setTotalActiveUsers] = useState(0);
  const [inactiveUsers, setTotalInactiveUsers] = useState(0);
  const [blockUsers, setTotalBlockUsers] = useState(0);
  const [overallAnalysis, setOverall] = useState({});
  const [users, setUsers] = useState([]);
  const [kycStat, setKycStat] = useState({});

  const fetchOverview = useCallback(async () => {
    // This was fetching overall-based-analysis which we are now replacing
    // hideLoader(); // Loader is handled in parent
  }, []);

  const getStat = useCallback(async () => {
    let res = await httpGet(`${customBaseUrl.authUrl}/api/v1/dashboard/users-analysis`);
    if (res?.status) {
      setTotalCorporateUsers(res?.data?.coorporateUsers);
      setTotalIndividualUsers(res?.data?.personalUsers);
      setTotalRegisteredUsers(res?.data?.registeredUsers);
      setTotalActiveUsers(res?.data?.activeUsers);
      setTotalInactiveUsers(res?.data?.inactiveUsers);
      setTotalBlockUsers(res?.data?.blockedUsers);
    }
  }, []);

  const handleGetUsers = useCallback(async () => {
    const res = await httpGet(`${customBaseUrl.authUrl}/api/v1/admin/users?page=0&size=10`);
    setUsers(res?._embedded?.userResponse || []);
  }, []);

  const getKycStat = useCallback(async () => {
    let res = await httpGet(`${customBaseUrl.kycUrl}/api/v1/admin/kyc/analysis`);
    if (res?.status) {
      setKycStat(res.data);
    }
  }, []);

  useEffect(() => {
    // handleGetUsers();
    fetchOverview();
    // getKycStat();
    // getStat();
  }, [handleGetUsers, fetchOverview, getKycStat, getStat]);

  // The effect in dashOverview for setting overall from analysis is no longer needed 
  // since we'll use analysis directly or transform it.

  const systemAnalysis = [
    {
      icon: <WalletIcon />,
      title: 'Total Transactions',
      value: { value: analysis?.totalTransactions ?? 0 },
      id: 1,
    },
    {
      icon: <WalletIcon />,
      title: 'Successful Transactions',
      value: { value: analysis?.successfulTransactions ?? 0 },
      id: 2,
    },
    {
      icon: <WalletIcon />,
      title: 'Pending Transactions',
      value: { value: analysis?.pendingTransactions ?? 0 },
      id: 3,
    },
    {
      icon: <WalletIcon />,
      title: 'Failed Transactions',
      value: { value: analysis?.failedTransactions ?? 0 },
      id: 4,
    },
  ];

  const renderTierStatsList = (stats) => [
    { icon: <WalletIcon />, title: 'Tier 1', value: { value: `${stats?.tier1 ?? 0} (${currencyFormat(stats?.tier1Amount ?? 0)})` }, id: 1 },
    { icon: <WalletIcon />, title: 'Tier 2', value: { value: `${stats?.tier2 ?? 0} (${currencyFormat(stats?.tier2Amount ?? 0)})` }, id: 2 },
    { icon: <WalletIcon />, title: 'Tier 3', value: { value: `${stats?.tier3 ?? 0} (${currencyFormat(stats?.tier3Amount ?? 0)})` }, id: 3 },
    { icon: <WalletIcon />, title: 'Tier 4', value: { value: `${stats?.tier4 ?? 0} (${currencyFormat(stats?.tier4Amount ?? 0)})` }, id: 4 },
    { icon: <CoinIcon />, title: 'Total', value: { value: `${stats?.total ?? 0} (${currencyFormat(stats?.totalAmount ?? 0)})` }, id: 5 },
  ];

  const userAnalysis = [
    { icon: <TempUserIcon />, title: 'Total Registered Users', value: { value: '', count: totalRegUsers }, id: 1 },
    { icon: <TempUserIcon />, title: 'Total Business Users', value: { value: '', count: totalCorporateUsers }, id: 2 },
    { icon: <TempUserIcon />, title: 'Total Personal Users', value: { value: '', count: totalIndividualUsers }, id: 3 },
    { icon: <TempUserIcon />, title: 'Total Active Users', value: { value: '', count: activeUsers }, id: 4 },
    { icon: <TempUserIcon />, title: 'Total Inactive Users', value: { value: '', count: inactiveUsers }, id: 5 },
    { icon: <TempUserIcon />, title: 'Total Blocked Users', value: { value: '', count: blockUsers }, id: 6 },
  ];

  const kycAnalysis = [
    { icon: <VerifiedUserIcon style={{ color: 'red' }} />, title: 'Total KYC Pending Approval', value: { value: '', count: kycStat?.pending }, id: 1 },
    { icon: <VerifiedUserIcon style={{ color: 'red' }} />, title: 'Total Rejected KYC', value: { value: '', count: kycStat?.rejected ?? 0 }, id: 2 },
    { icon: <VerifiedUserIcon style={{ color: 'red' }} />, title: 'Total KYC Approved T1', value: { value: '', count: kycStat?.tier1 }, id: 3 },
    { icon: <VerifiedUserIcon style={{ color: 'red' }} />, title: 'Total KYC Approved T2', value: { value: '', count: kycStat?.tier2 }, id: 4 },
    { icon: <VerifiedUserIcon style={{ color: 'red' }} />, title: 'Total KYC Approved T3', value: { value: '', count: kycStat?.tier3 }, id: 5 },
    { icon: <VerifiedUserIcon style={{ color: 'red' }} />, title: 'Total KYC Approved T4', value: { value: '', count: kycStat?.tier4 }, id: 6 },
  ];

  return (
    <>
      <div style={{ marginBottom: 10, borderRadius: 10 }}>
        <StatCards lists={systemAnalysis} state={state} title='System Transaction Overview' />
      </div>
      {tierData && (
        <>
          <div style={{ marginBottom: 10, borderRadius: 10 }}>
            <StatCards lists={renderTierStatsList(tierData.personal)} state={state} title='Personal Accounts Tier Analysis' />
          </div>
          <div style={{ marginBottom: 10, borderRadius: 10 }}>
            <StatCards lists={renderTierStatsList(tierData.nonCac)} state={state} title='Non-CAC Business Tier Analysis' />
          </div>
          <div style={{ marginBottom: 10, borderRadius: 10 }}>
            <StatCards lists={renderTierStatsList(tierData.cac)} state={state} title='CAC Business Tier Analysis' />
          </div>
          <div style={{ marginBottom: 10, borderRadius: 10 }}>
            <Card title="Overall Cumulative Summary">
              <Row gutter={16} key="overall-summary">
                <Col span={12} style={{ textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#BDBDBD' }}>Total Accounts</p>
                  <h4 style={{ margin: 0 }}>{tierData.total}</h4>
                </Col>
                <Col span={12} style={{ textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#BDBDBD' }}>Total Amount</p>
                  <h4 style={{ margin: 0 }}>{currencyFormat(tierData.totalAmount)}</h4>
                </Col>
              </Row>
            </Card>
          </div>
        </>
      )}
      <div style={{ marginBottom: 10, borderRadius: 10 }}>
        <StatCards lists={userAnalysis} state={state} title='Users Analysis' />
      </div>
      <div style={{ marginBottom: 10, borderRadius: 10 }}>
        <StatCards lists={kycAnalysis} state={state} title='KYC Analysis' />
      </div>
    </>
  );
}

export default DashboardOverView;
